import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, BadgeCheck, TrendingUp, ArrowLeft } from "lucide-react";
import api, { formatRON } from "@/lib/api";

export default function PremiumReport() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("loading"); // loading | ready | pending | error
  const [report, setReport] = useState(null);
  const polled = useRef(false);

  useEffect(() => {
    if (!sessionId || polled.current) return;
    polled.current = true;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const st = await api.get(`/checkout/status/${sessionId}`);
        if (st.data.payment_status === "paid") {
          const r = await api.get(`/valuation/premium-report/${sessionId}`);
          if (r.data.status === "paid" && r.data.report) { setReport(r.data.report); setState("ready"); return; }
        }
        if (st.data.status === "expired" || attempts >= 8) { setState("pending"); return; }
        setTimeout(poll, 2000);
      } catch { setState("error"); }
    };
    poll();
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="bg-slate-950 text-white min-h-screen" data-testid="premium-report-page">
      <div className="max-w-2xl mx-auto px-6 py-14">
        <Link to="/vinde" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-orange-500 transition-colors mb-8"><ArrowLeft className="w-4 h-4" /> Vinde Mașina</Link>
        <div className="flex items-center gap-2 mb-2">
          <BadgeCheck className="w-5 h-5 text-orange-500" />
          <span className="text-xs uppercase tracking-[0.3em] font-bold text-orange-500">{t("valuation.premiumTitle")}</span>
        </div>

        {state === "loading" && <div className="py-16 text-center text-slate-400"><Loader2 className="w-8 h-8 animate-spin mx-auto text-orange-500" /><p className="mt-4">{t("valuation.generating")}</p></div>}
        {state === "pending" && <div className="border border-amber-500/40 bg-amber-500/10 text-amber-300 p-6 mt-6">Plata se procesează. Reîncarcă pagina în câteva momente.</div>}
        {state === "error" && <div className="border border-red-500/40 bg-red-500/10 text-red-300 p-6 mt-6">Nu am putut încărca raportul. Contactează-ne dacă plata a fost efectuată.</div>}

        {state === "ready" && report && (
          <div className="mt-6" data-testid="premium-report-content">
            <div className="border border-orange-500/40 bg-orange-950/20 p-6">
              <div className="text-xs uppercase tracking-wide text-slate-400">{t("valuation.fairValue")}</div>
              <div className="text-4xl font-black text-orange-400 mt-1">{report.fair_value ? formatRON(report.fair_value) : "—"}</div>
              <div className="text-sm text-slate-300 mt-2">{t("valuation.tightRange")}: <b>{formatRON(report.low)} – {formatRON(report.high)}</b></div>
            </div>
            {report.summary && <p className="text-slate-300 mt-6 leading-relaxed">{report.summary}</p>}
            {report.factors?.length > 0 && (
              <div className="mt-6">
                <div className="text-xs uppercase tracking-wide font-bold text-slate-500 mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> {t("valuation.factors")}</div>
                <ul className="space-y-2">
                  {report.factors.map((f, i) => <li key={i} className="text-sm text-slate-300 flex gap-2"><span className="text-orange-500">▸</span>{f}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
